// DynamicFormEditable.tsx (Label-Value-Pair layout with old and editable new value)
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  TextField,
  MenuItem,
  Checkbox,
  Typography,
  Paper,
  TableContainer
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateEndorsementFormData,
  selectEndorsmentFormData
} from 'src/features/rfqDataSlice';
import AdapterDateFns from '@mui/lab/AdapterDateFns';

function DynamicFormEditable({ policyDetails, formNumber }) {
  const dispatch = useDispatch();
  const endorsementFormData = useSelector(selectEndorsmentFormData);

  const [editableFields, setEditableFields] = useState([]);

  useEffect(() => {
    let selectedFields = [];
    if (formNumber === 1) {
      selectedFields = endorsementFormData?.dynamicFormData || [];
    } else if (formNumber === 2) {
      selectedFields = endorsementFormData?.secondDynamicFormData || [];
    } else if (formNumber === 3) {
      selectedFields = endorsementFormData?.thirdDynamicFormData || [];
    }
    setEditableFields(selectedFields);
  }, [endorsementFormData, formNumber]);

  const formDataSource =
    formNumber === 1
      ? policyDetails?.dynamicFormData
      : formNumber === 2
        ? policyDetails?.secondDynamicFormData
        : formNumber === 3
          ? policyDetails?.thirdDynamicFormData
          : [];

  const isEditable = (groupIndex, fieldIndex) => {
    const rowId = `${groupIndex}-${fieldIndex}`;
    return editableFields.some((item) => item.id === rowId);
  };

  const getEditableItem = (groupIndex, fieldIndex) => {
    const rowId = `${groupIndex}-${fieldIndex}`;
    return editableFields.find((item) => item.id === rowId);
  };

  const handleValueChange = (groupIndex, fieldIndex, newValue) => {
    const rowId = `${groupIndex}-${fieldIndex}`;
    const updated = editableFields.map((item) =>
      item.id === rowId ? { ...item, data: { ...item.data, value: newValue } } : item
    );
    setEditableFields(updated);

    const key =
      formNumber === 1
        ? 'dynamicFormData'
        : formNumber === 2
          ? 'secondDynamicFormData'
          : 'thirdDynamicFormData';

    dispatch(updateEndorsementFormData({ [key]: updated }));
  };

  const renderEditableInput = (formInput, value, onChange) => {
    switch (formInput.type) {
      case 'TextField':
      case 'Textarea':
        return (
          <TextField
            size="small"
            multiline={formInput.type === 'Textarea'}
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case 'Dropdown':
        return (
          <TextField
            size="small"
            select
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            {formInput.options.map((opt, idx) => (
              <MenuItem key={idx} value={opt.value}>
                {opt.value}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'MultiSelectDropdown':
        return (
          <TextField
            size="small"
            select
            fullWidth
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
            }}
            value={Array.isArray(value) ? value : []}
            onChange={(e) => onChange(e.target.value)}
          >
            {formInput.options.map((opt, idx) => (
              <MenuItem key={idx} value={opt.value}>
                <Checkbox checked={value?.includes(opt.value)} />
                {opt.value}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'Checkbox': {
        // value is expected to be an array of objects with key and value
        const selectedValues = Array.isArray(value) ? value.map(v => v.value) : [];

        const handleCheckboxChange = (checkedValue) => {
          let updatedArray = [];
          if (selectedValues.includes(checkedValue)) {
            updatedArray = value.filter(item => item.value !== checkedValue);
          } else {
            updatedArray = [...(value || []), { key: '', value: checkedValue }];
          }
          onChange(updatedArray);
        };

        return (
          <div>
            {formInput.options.map((opt, idx) => (
              <div key={idx}>
                <Checkbox
                  checked={selectedValues.includes(opt.value)}
                  onChange={() => handleCheckboxChange(opt.value)}
                />
                <span>{opt.value}</span>
              </div>
            ))}
          </div>
        );
      }
      case 'DatePicker':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              views={['year', 'month', 'day']}
              inputFormat="dd/MM/yyyy"  // ✓ correct for AdapterDateFns
              value={
                moment(value, "DD/MM/YYYY").isValid()
                  ? moment(value, "DD/MM/YYYY").toDate()  // ✓ convert moment → Date
                  : null
              }
              onChange={(newDate) => {
                const formatted = newDate
                  ? moment(newDate).format("DD/MM/YYYY") // store as DD/MM/YYYY
                  : "";
                onChange(formatted);
              }}
              renderInput={(params) => (
                <TextField
                  fullWidth
                  size="small"
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    placeholder: "DD/MM/YYYY",
                  }}
                />
              )}
            />
          </LocalizationProvider>

        );
      default:
        return value || 'N/A';
    }
  };

  const renderValue = (value, type) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return 'N/A';

    if (type === 'DatePicker' && moment(value).isValid()) {
      return moment(value).format('DD-MM-YYYY');
    }

    if (Array.isArray(value)) {
      const stringValues = value.map((v) => {
        if (typeof v === 'string') {
          return v.trim();
        }
        if (typeof v === 'object' && v !== null && 'value' in v) {
          return v.value.trim?.();
        }
        return null;
      }).filter(v => !!v);

      return stringValues.length > 0 ? stringValues.join(', ') : 'N/A';
    }

    return typeof value === 'string' && value.trim() === '' ? 'N/A' : value;
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">

        <TableBody>
          {formDataSource?.map((form, groupIndex) => (
            <React.Fragment key={groupIndex}>
              {form?.groupName && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ background: 'rgb(9 84 174)', color: '#fff', fontWeight: 'bold' }}>
                    {form.groupName}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell style={{ width: '470px' }}><strong>Field</strong></TableCell>
                <TableCell sx={{ color: 'red' }}><strong>Old Value</strong></TableCell>
                <TableCell sx={{ color: 'green' }}><strong>New Value</strong></TableCell>
              </TableRow>
              {form?.masterForms?.map((formInput, fieldIndex) => {
                const editable = isEditable(groupIndex, fieldIndex);
                const editableItem = getEditableItem(groupIndex, fieldIndex);
                const currentValue = editableItem?.data?.value ?? '';
                const originalValue = formInput?.value ?? '';

                return (
                  editable && (
                    <TableRow key={`${groupIndex}-${fieldIndex}`}>
                      <TableCell style={{ width: '470px' }}>{formInput?.label}</TableCell>
                      <TableCell style={{ width: '450px' }} sx={{ color: originalValue !== currentValue ? 'red' : 'inherit' }}>
                        {/* {Array.isArray(originalValue)
                          ? originalValue
                            .map((v) => v?.value?.trim())
                            .filter((v) => !!v) // remove empty/undefined/null
                            .join(', ') || 'N/A'
                          : originalValue || 'N/A'} */}
                        {renderValue(originalValue, formInput.type)}
                      </TableCell>
                      <TableCell>
                        {renderEditableInput(formInput, currentValue, (val) => handleValueChange(groupIndex, fieldIndex, val))}
                      </TableCell>
                    </TableRow>
                  )
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DynamicFormEditable;
